// src/services/communityService.ts
// The StrymHub Community — the repertory of members the studio casts and crews from.

import { buildQuery, studioRequest } from './studioApi';
import type {
  CommunityStats,
  MemberProfile,
  MemberProfilePayload,
  PagedResult,
} from '../types/studio';

export interface DirectoryQuery {
  craft?: string;
  search?: string;
  page?: number;
  size?: number;
}

export const communityService = {
  getStats(signal?: AbortSignal): Promise<CommunityStats> {
    return studioRequest<CommunityStats>('/community/stats', { withAuth: false, signal });
  },

  getMembers(query: DirectoryQuery = {}, signal?: AbortSignal): Promise<PagedResult<MemberProfile>> {
    const search = buildQuery({
      craft: query.craft,
      search: query.search,
      page: query.page ?? 0,
      size: query.size ?? 24,
    });
    return studioRequest<PagedResult<MemberProfile>>(`/community/members${search}`, {
      withAuth: false,
      signal,
    });
  },

  getMember(slug: string, signal?: AbortSignal): Promise<MemberProfile> {
    return studioRequest<MemberProfile>(`/community/members/${encodeURIComponent(slug)}`, {
      withAuth: false,
      signal,
    });
  },

  getMyProfile(signal?: AbortSignal): Promise<MemberProfile> {
    return studioRequest<MemberProfile>('/community/me', { signal });
  },

  hasProfile(signal?: AbortSignal): Promise<boolean> {
    return studioRequest<{ hasProfile: boolean }>('/community/me/exists', { signal }).then(
      (result) => result.hasProfile,
    );
  },

  join(payload: MemberProfilePayload): Promise<MemberProfile> {
    return studioRequest<MemberProfile>('/community/me', { method: 'POST', body: payload });
  },

  updateMyProfile(payload: MemberProfilePayload): Promise<MemberProfile> {
    return studioRequest<MemberProfile>('/community/me', { method: 'PUT', body: payload });
  },

  setVisibility(visible: boolean): Promise<MemberProfile> {
    return studioRequest<MemberProfile>(`/community/me/visibility${buildQuery({ visible })}`, {
      method: 'PATCH',
    });
  },
};

export default communityService;
